import { produce } from "immer";
import type {
  LocalVideoTrack,
  LocalAudioTrack,
  Room,
  VideoBandwidthProfileOptions,
  Track,
  Participant,
} from "twilio-video";

import type { InterviewActions } from "./actions/dispatches";

export type JoiningInterview = {
  state: "joining";
  interview_id: string;
};

export type IJoinedInterview = Omit<JoiningInterview, "state"> & {
  state: "joined";
  room: Room;
  selectedParticipant: Participant | null;
  twilio_room_id: string;
};

export type InactiveInterview = {
  state: "inactive";
};

export type InterviewState = JoiningInterview | IJoinedInterview | InactiveInterview;

export type PreferredMediaDevices = {
  video: string;
  audioInput: string;
  audioOutput: string;
};

export interface TwilioRoomSettings {
  trackSwitchOffMode: VideoBandwidthProfileOptions["trackSwitchOffMode"];
  dominantSpeakerPriority?: Track.Priority;
  bandwidthProfileMode: VideoBandwidthProfileOptions["mode"];
  maxAudioBitrate: number;
  contentPreferencesMode?: "auto" | "manual";
  clientTrackSwitchOffControl?: "auto" | "manual";
}

export type PendingLocalTracks = {
  state: "pending";
};

export type CreatingLocalTracks = Omit<PendingLocalTracks, "state"> & {
  state: "creating";
};

export type CreatedLocalTracks = Omit<PendingLocalTracks, "state"> & {
  state: "created";
  video: LocalVideoTrack | null;
  audio: LocalAudioTrack | null;
  videoEnabled: boolean;
  audioEnabled: boolean;
};

export type LocalTracksState = PendingLocalTracks | CreatingLocalTracks | CreatedLocalTracks;

export type ScreenSharingState =
  | {
      active: true;
      cancel: () => void;
    }
  | {
      active: false;
    };

const initialState: {
  interview: InterviewState;
  preferredDeviceIds: PreferredMediaDevices;
  localTracks: LocalTracksState;
  roomSettings: TwilioRoomSettings;
  screenSharing: ScreenSharingState;
} = {
  interview: {
    state: "inactive",
  },
  preferredDeviceIds: {
    video: "default",
    audioInput: "default",
    audioOutput: "default",
  },
  localTracks: {
    state: "pending",
  },
  roomSettings: {
    trackSwitchOffMode: undefined,
    dominantSpeakerPriority: "standard",
    bandwidthProfileMode: "collaboration",
    maxAudioBitrate: 16000,
    contentPreferencesMode: "auto",
    clientTrackSwitchOffControl: "auto",
  },
  screenSharing: {
    active: false,
  },
};

export const interviewReducer = produce((draft: typeof initialState, action: InterviewActions) => {
  switch (action.type) {
    case "START_JOINING_INTERVIEW": {
      draft.interview = {
        state: "joining",
        ...action.payload,
      };
      break;
    }
    case "JOINED_INTERVIEW": {
      if (draft.interview.state === "joining") {
        draft.interview = {
          ...draft.interview,
          state: "joined",
          room: action.payload.room,
          twilio_room_id: action.payload.room_id,
          selectedParticipant: null,
        };
      }
      break;
    }
    case "LEFT_INTERVIEW": {
      draft.interview = {
        state: "inactive",
      };
      break;
    }
    case "SET_ACTIVE_PARTICIPANT": {
      if (draft.interview.state === "joined") {
        draft.interview.selectedParticipant = action.payload as Participant;
      }
      break;
    }
    case "UPDATE_ROOM_SETTINGS": {
      Object.assign(draft.roomSettings, action.payload);
      break;
    }
    case "UPDATE_PREFERRED_DEVICE_IDS": {
      Object.assign(draft.preferredDeviceIds, action.payload);
      break;
    }
    case "CREATING_LOCAL_TRACKS": {
      draft.localTracks.state = "creating";
      break;
    }
    case "CREATED_LOCAL_TRACKS": {
      draft.localTracks = {
        ...draft.localTracks,
        state: "created",
        video: action.payload.video,
        audio: action.payload.audio,
        videoEnabled: !!action.payload.video,
        audioEnabled: !!action.payload.audio,
      };
      break;
    }
    case "SET_VIDEO_ENABLED": {
      if (draft.localTracks.state === "created") {
        draft.localTracks.videoEnabled = action.payload;
      }
      break;
    }
    case "SET_AUDIO_ENABLED": {
      if (draft.localTracks.state === "created") {
        draft.localTracks.audioEnabled = action.payload;
      }
      break;
    }
    case "STOP_LOCAL_TRACKS": {
      draft.localTracks = {
        state: "pending",
      };
      break;
    }
    case "START_SCREEN_SHARING": {
      draft.screenSharing = {
        active: true,
        cancel: action.payload,
      };
      break;
    }
    case "STOP_SCREEN_SHARING": {
      draft.screenSharing = {
        active: false,
      };
      break;
    }
    default:
      break;
  }
}, initialState);
